import React from 'react';
import { WorkforceGroupFormProps } from './workforce-group-form';
import {
  Banner,
  Card,
  Checkbox,
  Gutter,
  Gap,
  InputNumber,
  Paragraph,
  Text,
} from '@common/components';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

type Props = {
  onUpdateInputData: WorkforceGroupFormProps['onUpdateInputData'];
  consecutiveHoursLimit?: number;
  consecutiveHoursBridgeTolerance?: number;
  consecutiveHoursResetPeriod?: number;
};

const CONSECUTIVE_HOURS_FIELDS = [
  {
    label: 'Maximum consecutive hours',
    key: 'consecutiveHoursLimit' as const,
    min: 0,
    max: 48,
    step: 1,
    help: 'The maximum total hours a worker can work without a qualifying break.',
  },
  {
    label: 'Bridge gaps up to (hours)',
    key: 'consecutiveHoursBridgeTolerance' as const,
    min: 0,
    max: 12,
    step: 0.25,
    help: 'Breaks up to this length are treated as continuous work.',
  },
  {
    label: 'Minimum rest to reset (hours)',
    key: 'consecutiveHoursResetPeriod' as const,
    min: 0,
    max: 24,
    step: 0.25,
    help: 'Breaks must be at least this long to reset the consecutive hours count. Usually greater than the bridge gap to enforce a rest period between shifts.',
  },
];

export const WorkforceGroupFormConsecutiveHoursTab = ({
  onUpdateInputData,
  consecutiveHoursLimit,
  consecutiveHoursBridgeTolerance,
  consecutiveHoursResetPeriod,
}: Props) => {
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    if (consecutiveHoursLimit && consecutiveHoursLimit > 0) setChecked(true);
  }, [consecutiveHoursLimit]);

  const onChange = (e: CheckboxChangeEvent) => {
    setChecked(e.target.checked);
    if (!e.target.checked) {
      onUpdateInputData('consecutiveHoursLimit', 0);
      onUpdateInputData('consecutiveHoursBridgeTolerance', 0);
      onUpdateInputData('consecutiveHoursResetPeriod', 0);
    }
  };

  const values = {
    consecutiveHoursLimit,
    consecutiveHoursBridgeTolerance,
    consecutiveHoursResetPeriod,
  };

  return (
    <Gutter direction="vertical" size="md">
      <Card>
        <Gap vertical size="lg">
          <Checkbox checked={checked} onChange={onChange}>
            Limit consecutive working hours
          </Checkbox>
          <Paragraph>
            When enabled, staff cannot claim shifts that cause them to exceed
            the set consecutive hours limit.
          </Paragraph>
          {CONSECUTIVE_HOURS_FIELDS.map(
            ({ label, key, min, max, step, help }) => (
              <label key={key} style={{ width: '100%' }}>
                <Gap vertical fullWidth size="xs">
                  <Text weight="bold">{label}</Text>
                  <InputNumber
                    min={min}
                    max={max}
                    step={step}
                    value={values[key]}
                    onChange={(value) =>
                      onUpdateInputData(key, Number(value ?? 0))
                    }
                    disabled={!checked}
                    size="large"
                    style={{ width: 115 }}
                  />
                  <Paragraph color="midnightDarkest">{help}</Paragraph>
                </Gap>
              </label>
            )
          )}
          <Banner type="info">
            Shift claims are blocked if the break is longer than the bridge gap
            but shorter than the reset period.
          </Banner>
        </Gap>
      </Card>
    </Gutter>
  );
};
